// Hybrid encryption for sensitive form data (VAPT: "PII transmitted in cleartext").
//
// The browser generates a one-time AES-256-GCM key, encrypts the PII with it, then wraps
// that AES key with the server's RSA public key (RSA-OAEP). Only the server, holding the
// matching private key (RSA_PRIVATE_KEY_B64), can unwrap the AES key and read the data.
// This keeps PII unreadable in the request body even after TLS is terminated (proxies,
// logs, intercepting tools). Uses the built-in Web Crypto API — no third-party library.
//
// The public key is safe to ship in the client bundle. Keep it in sync with the private
// key configured on Email_service. For production, generate a FRESH key pair.

const PUBLIC_KEY_PEM = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAyWzcIi6lYQL2AyoZfKw3
m2gHUVXLHFvhIiAHZ/F0REKaXo3b0AdBHwW0S3RCkv/Z5LAK2BX2K9VdC0Hitpqc
kjH5fwGA0QYidqW5g9N/tYMuCMNk33TRcvg2pU3fK5Ar6wTiBLqFCIiUCygIwWG8
nOWJMzNgTXVRJKu4MAaZeSS69NuzBNV7rx/SCFhwoAOqryIMU4L7Lb3wfl6zc3fs
PAw3AqGj1cCKsX7ZAVoj/z2pBsgLPY194UAGLDQN4lcR+W7Wq303cfbVdsQmBC6b
M6JUS8uC/1z8QZzLo7pUhVfR6PfzTOYE23SfXF7lmGjTfr7NofxHHH+vW2tREcdv
yQIDAQAB
-----END PUBLIC KEY-----`;

const toBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
};

// base64url (no padding) — the encoding JWE uses for each segment.
const toBase64Url = (buffer) =>
  toBase64(buffer).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

const pemToArrayBuffer = (pem) => {
  const base64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/, "")
    .replace(/-----END PUBLIC KEY-----/, "")
    .replace(/\s+/g, "");
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
};

let publicKeyPromise;
const getPublicKey = () => {
  if (!publicKeyPromise) {
    publicKeyPromise = crypto.subtle.importKey(
      "spki",
      pemToArrayBuffer(PUBLIC_KEY_PEM),
      { name: "RSA-OAEP", hash: "SHA-256" },
      false,
      ["encrypt"]
    );
  }
  return publicKeyPromise;
};

// Encrypts an object and returns a single JWE Compact Serialization string:
//   BASE64URL(header).BASE64URL(encrypted_key).BASE64URL(iv).BASE64URL(ciphertext).BASE64URL(tag)
// This is the standard JWE format (alg: RSA-OAEP-256, enc: A256GCM) — one opaque token
// instead of separate k/iv/data fields.
export async function encryptSensitive(data) {
  if (!crypto?.subtle) {
    throw new Error("Secure encryption is not supported by this browser.");
  }

  // Protected header, base64url-encoded; also used as the GCM Additional Authenticated Data (per JWE).
  const encodedHeader = toBase64Url(
    new TextEncoder().encode(JSON.stringify({ alg: "RSA-OAEP-256", enc: "A256GCM" }))
  );
  const aad = new TextEncoder().encode(encodedHeader);

  const plaintext = new TextEncoder().encode(JSON.stringify(data));

  const aesKey = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt"]
  );

  const iv = crypto.getRandomValues(new Uint8Array(12));
  // Web Crypto returns ciphertext||tag; JWE carries them as separate segments, so split the 16-byte tag.
  const ctWithTag = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: aad, tagLength: 128 }, aesKey, plaintext)
  );
  const ciphertext = ctWithTag.slice(0, ctWithTag.length - 16);
  const tag = ctWithTag.slice(ctWithTag.length - 16);

  const rawAesKey = await crypto.subtle.exportKey("raw", aesKey);
  const encryptedKey = await crypto.subtle.encrypt({ name: "RSA-OAEP" }, await getPublicKey(), rawAesKey);

  return [
    encodedHeader,
    toBase64Url(encryptedKey),
    toBase64Url(iv),
    toBase64Url(ciphertext),
    toBase64Url(tag),
  ].join(".");
}
